import { useTranslation } from '@silospot/i18n/react';
import type { OnboardingResume } from '@silospot/shared';
import { toast } from '@silospot/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2, Megaphone, RotateCcw, ShieldCheck } from 'lucide-react';
import { type ReactElement, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type RestorableSite, api, restorableFromError, serverError } from '../../lib/api';
import { useProjectStore } from '../../stores/project-store';
import { GadsFlow } from './gads-flow';
import { TrackerInstall } from './tracker-install';

interface WizProject {
  id: string;
  domain: string;
  publicKey: string;
  secretKey: string;
}

/**
 * Linear, mandatory onboarding wizard (C-fix2). Bornes Welcome/Done + 3 numbered
 * steps. Wired to REAL endpoints (createProject / verify / Google Ads pool+link
 * from C-fix1) — no localStorage, no fake data. Closable; the dashboard lock is
 * enforced separately by the gate. Cloudflare 1-click + Meta/Bing are shown
 * disabled ("soon").
 */
export function OnboardingWizard({
  onClose,
  resume,
}: {
  onClose: () => void;
  resume?: OnboardingResume | null;
}): ReactElement {
  const { t } = useTranslation('pages');
  const qc = useQueryClient();

  const seeded: WizProject | null = resume
    ? {
        id: resume.projectId,
        domain: resume.domain,
        publicKey: resume.publicKey,
        secretKey: resume.secretKey,
      }
    : null;
  const navigate = useNavigate();
  const setCurrentProjectId = useProjectStore((s) => s.setCurrentProjectId);
  const [step, setStep] = useState(resume ? (resume.step === 'tracker' ? 2 : 3) : 0);
  const [project, setProject] = useState<WizProject | null>(seeded);
  const [domain, setDomain] = useState('');
  const [restorable, setRestorable] = useState<RestorableSite | null>(null);

  // While the wizard is open, the top site selector must reflect the site being
  // set up (not whichever site was previously selected).
  useEffect(() => {
    if (project?.id) setCurrentProjectId(project.id);
  }, [project?.id, setCurrentProjectId]);

  const clean = domain
    .trim()
    .replace(/^https?:\/\//, '')
    .replace(/\/.*$/, '');
  const domainName = project?.domain.replace(/^https?:\/\//, '') ?? clean;

  const refreshOnboarding = () => {
    void qc.invalidateQueries({ queryKey: ['onboarding'] });
    void qc.invalidateQueries({ queryKey: ['projects'] });
  };

  const onCreated = (p: {
    id: string;
    primaryDomain: string;
    publicKey: string;
    secretKey: string;
  }) => {
    setRestorable(null);
    setProject({
      id: p.id,
      domain: p.primaryDomain,
      publicKey: p.publicKey,
      secretKey: p.secretKey,
    });
    refreshOnboarding();
    setStep(2);
  };
  const onCreateError = (e: unknown) => {
    // A deleted site with this domain can be restored — prompt instead of erroring.
    const site = restorableFromError(e);
    if (site) {
      setRestorable(site);
      return;
    }
    // Only a real validation failure (400) means "bad domain". Anything else
    // (plan limit 403, conflict, server error) must surface the actual reason
    // instead of the misleading "invalid domain".
    const err = e as { status?: number; message?: string };
    toast.error(
      err?.status === 400
        ? t('onb.invalidDomain')
        : err?.status === 409
          ? t('onb.domainExists')
          : err?.message || t('onb.createFailed'),
    );
  };

  const body = () => ({ name: clean, primaryDomain: `https://${clean}`, additionalDomains: [] });
  const create = useMutation({
    mutationFn: () => api.createProject(body()),
    onSuccess: ({ project: p }) => onCreated(p),
    onError: onCreateError,
  });
  // "Start over" from the restore prompt: a fresh, empty project on this domain.
  const createFresh = useMutation({
    mutationFn: () => api.createProject(body(), { fresh: true }),
    onSuccess: ({ project: p }) => onCreated(p),
    onError: onCreateError,
  });

  // Restore the deleted site (same data + history) instead of starting over.
  const restore = useMutation({
    mutationFn: () => api.restoreProject((restorable as RestorableSite).projectId),
    onSuccess: ({ project: p }) => {
      const id = p.id;
      setRestorable(null);
      toast.success(t('onb.restoreDone'));
      refreshOnboarding();
      setCurrentProjectId(id);
      onClose();
      navigate(`/projects/${id}`);
    },
    onError: (e) => toast.error(serverError(e) ?? t('onb.createFailed')),
  });

  // Step 3 — connect status; the full account→campaigns→recap flow is GadsFlow.
  const statusQ = useQuery({
    queryKey: ['gads-status'],
    queryFn: () => api.googleAdsStatus(),
    enabled: step === 3,
  });
  const connected = !!statusQ.data?.connected;
  const connect = useMutation({
    // Return INTO the wizard on THIS site at the Ads step (?wiz), not the bare route.
    mutationFn: () =>
      api.googleAdsConnect(
        project ? `/projects/${project.id}?wiz=${project.id}` : window.location.pathname,
      ),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: (e) => toast.error(serverError(e) ?? t('gads.toastError')),
  });

  // Once Google Ads (step 3) is validated or skipped, close the wizard and land
  // the user on the new site's dashboard — automatic, no manual "open" click.
  const finishToDashboard = () => {
    refreshOnboarding();
    onClose();
    if (project) navigate(`/projects/${project.id}`);
  };

  const RAIL = [t('onb.railWelcome'), t('onb.railAddSite'), t('onb.railTracker'), t('onb.railAds')];

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-[rgba(15,21,32,0.55)] p-4">
      {restorable ? (
        <div className="absolute inset-0 z-[130] flex items-center justify-center bg-[rgba(15,21,32,0.7)] p-4">
          <div className="w-full max-w-md rounded-2xl bg-surface p-6 shadow-[0_24px_64px_rgba(16,24,40,0.28)]">
            <div className="mb-3 flex items-center gap-2.5">
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-accent-bg text-primary">
                <RotateCcw className="h-4 w-4" />
              </span>
              <div className="text-[16px] font-bold tracking-[-0.02em] text-text">
                {t('onb.restoreTitle')}
              </div>
            </div>
            <p className="mb-1.5 text-[13px] leading-relaxed text-text-2">
              {t('onb.restoreBody', {
                domain: restorable.domain.replace(/^https?:\/\//, ''),
                visits: String(restorable.visits),
              })}
            </p>
            <p className="mb-5 min-h-[16px] text-[12px] text-text-3">
              {restorable.deletedAt
                ? t('onb.restoreSince', {
                    date: new Date(restorable.deletedAt).toLocaleDateString(),
                  })
                : ''}
            </p>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setRestorable(null);
                  createFresh.mutate();
                }}
                disabled={createFresh.isPending || restore.isPending}
                className="rounded-xl border border-border px-4 py-2 text-[13px] font-semibold text-text-2 hover:bg-neutral-bg disabled:opacity-50"
              >
                {t('onb.restoreFresh')}
              </button>
              <button
                type="button"
                onClick={() => restore.mutate()}
                disabled={restore.isPending}
                className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-[13px] font-semibold text-white hover:opacity-90 disabled:opacity-50"
              >
                {restore.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  t('onb.restoreYes')
                )}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <div className="flex max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-surface shadow-[0_24px_64px_rgba(16,24,40,0.28)]">
        {/* Rail */}
        <aside className="hidden w-[224px] flex-col justify-between bg-bg p-5 sm:flex">
          <div>
            <div className="mb-6 flex items-center gap-2">
              <span className="grid h-7 w-7 place-items-center rounded-lg bg-primary text-white">
                <ShieldCheck className="h-4 w-4" />
              </span>
              <span className="text-[15px] font-bold tracking-[-0.02em]">Silospot</span>
            </div>
            <div className="flex flex-col gap-1">
              {RAIL.map((label, i) => {
                const state = i < step ? 'done' : i === step ? 'cur' : 'todo';
                return (
                  <div
                    key={label}
                    className={`flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-[12.5px] ${state === 'cur' ? 'bg-accent-bg font-semibold text-primary' : 'text-text-3'}`}
                  >
                    <span
                      className={`grid h-5 w-5 shrink-0 place-items-center rounded-full text-[10.5px] ${state === 'done' ? 'bg-clean-fg text-white' : state === 'cur' ? 'bg-primary text-white' : 'bg-neutral-bg text-text-3'}`}
                    >
                      {i < step ? (
                        <Check className="h-3 w-3" />
                      ) : i >= 1 && i <= 3 ? (
                        i
                      ) : (
                        <span className="h-1.5 w-1.5 rounded-full bg-current" />
                      )}
                    </span>
                    {label}
                  </div>
                );
              })}
            </div>
          </div>
          <p className="text-[11.5px] leading-relaxed text-text-3">{t('onb.railFoot')}</p>
        </aside>

        {/* Body */}
        <div className="relative flex-1 p-7">
          <button
            type="button"
            aria-label={t('onb.close')}
            onClick={onClose}
            className="absolute right-4 top-4 grid h-8 w-8 place-items-center rounded-md text-text-3 hover:bg-bg"
          >
            ✕
          </button>

          {step === 0 ? (
            <div>
              <span className="mb-4 grid h-12 w-12 place-items-center rounded-2xl bg-accent-bg text-primary">
                <ShieldCheck className="h-6 w-6" />
              </span>
              <h1 className="text-[22px] font-bold tracking-[-0.02em] text-text">
                {t('onb.wTitle')}
              </h1>
              <p className="mt-2 max-w-md text-[13.5px] leading-relaxed text-text-2">
                {t('onb.wLead')}
              </p>
              <div className="mt-5 flex flex-col gap-2.5">
                {[
                  ['wS1T', 'wS1B'],
                  ['wS2T', 'wS2B'],
                  ['wS3T', 'wS3B'],
                ].map(([tk, bk], i) => (
                  <div
                    key={tk}
                    className="flex items-center gap-3 rounded-xl border border-border bg-bg/40 px-3.5 py-2.5"
                  >
                    <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-accent-bg text-[12px] font-bold text-primary">
                      {i + 1}
                    </span>
                    <div>
                      <div className="text-[13.5px] font-semibold text-text">{t(`onb.${tk}`)}</div>
                      <div className="text-[12px] text-text-3">{t(`onb.${bk}`)}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex justify-end">
                <button type="button" onClick={() => setStep(1)} className={btnPrimary}>
                  {t('onb.wStart')}
                </button>
              </div>
            </div>
          ) : step === 1 ? (
            <div>
              <div className={kicker}>{t('onb.railAddSite')} · 1/3</div>
              <h1 className={h1}>{t('onb.s1Title')}</h1>
              <p className={lead}>{t('onb.s1Lead')}</p>
              <label className={fieldLabel} htmlFor="onb-domain">
                {t('onb.s1DomainLabel')}
              </label>
              <div className="flex items-center overflow-hidden rounded-[10px] border border-border-strong">
                <span className="bg-bg px-3 py-2.5 text-[13px] text-text-3">https://</span>
                <input
                  id="onb-domain"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  placeholder="mon-site.com"
                  className="w-full px-3 py-2.5 text-[14px] outline-none"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && clean) create.mutate();
                  }}
                />
              </div>
              <div className="mt-6 flex items-center justify-between">
                <button type="button" onClick={() => setStep(0)} className={btnGhost}>
                  {t('onb.back')}
                </button>
                <button
                  type="button"
                  disabled={!clean || create.isPending}
                  onClick={() => create.mutate()}
                  className={btnPrimary}
                >
                  {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {t('onb.continue')}
                </button>
              </div>
            </div>
          ) : step === 2 ? (
            <div>
              <div className={kicker}>{t('onb.railTracker')} · 2/3</div>
              {project ? (
                <TrackerInstall
                  projectId={project.id}
                  projectDomain={domainName}
                  publicKey={project.publicKey}
                  secretKey={project.secretKey}
                  embedded
                  onboarding
                  onDone={() => {
                    refreshOnboarding();
                    setStep(3);
                  }}
                  onFinishLater={() => {
                    toast.info(t('onb.trackerPending'));
                    finishToDashboard();
                  }}
                />
              ) : null}
            </div>
          ) : step === 3 ? (
            <div>
              <div className={kicker}>{t('onb.railAds')} · 3/3</div>
              <h1 className={h1}>{t('onb.s3Title')}</h1>
              <p className={lead}>{t('onb.s3Lead')}</p>

              {!connected ? (
                <div>
                  <button
                    type="button"
                    disabled={connect.isPending || statusQ.isLoading}
                    onClick={() => connect.mutate()}
                    className={btnPrimary}
                  >
                    {connect.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    {t('onb.s3Title')}
                  </button>
                  <p className="mt-2 text-[12px] text-text-3">{t('gads.readonly')}</p>
                </div>
              ) : project ? (
                <GadsFlow
                  projectId={project.id}
                  projectName={domainName}
                  onDone={() => finishToDashboard()}
                  onSkip={() => {
                    toast.info(t('onb.adsSkipped'));
                    finishToDashboard();
                  }}
                />
              ) : null}

              <div className="mt-4 flex items-center gap-2 rounded-lg border border-border bg-bg/40 px-3 py-2 text-[11.5px] text-text-3">
                <Megaphone className="h-3.5 w-3.5" /> {t('onb.metaBingSoon')}
              </div>

              {!connected ? (
                <div className="mt-5">
                  <button
                    type="button"
                    onClick={() => setStep(resume ? 3 : 2)}
                    disabled={!!resume}
                    className={btnGhost}
                  >
                    {t('onb.back')}
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

const btnPrimary =
  'inline-flex items-center gap-2 rounded-[10px] bg-primary px-4 py-2.5 text-[13.5px] font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50';
const btnGhost =
  'inline-flex items-center gap-2 rounded-[10px] px-3 py-2.5 text-[13.5px] font-semibold text-text-2 hover:bg-bg disabled:opacity-40';
const kicker = 'text-[11px] font-semibold uppercase tracking-[0.06em] text-primary';
const h1 = 'mt-1 text-[20px] font-bold tracking-[-0.02em] text-text';
const lead = 'mb-4 mt-1.5 max-w-md text-[13.5px] leading-relaxed text-text-2';
const fieldLabel = 'mb-1.5 block text-[12px] font-semibold text-text-2';
